/**
 * Canonical analytics event names.
 *
 * Convention: `lyt-<domain>-<action>` (kebab-case, verb-last).
 *
 * These names are shared across both analytics channels — GA4 custom events
 * and Microsoft Clarity beacons — via `trackEvent` from `src/utils/analytics.js`.
 * GA4 requires event names in kebab/snake case so this shape is authoritative.
 */
const ANALYTIC_EVENTS = {
    // Map Search Events
    MAP_SEARCH_FOCUSED: 'lyt-map-search-focus',
    MAP_SEARCH_ITEM_SELECTED: 'lyt-map-search-item-select',

    // Map Interaction Events
    MAP_VEHICLE_CLICKED: 'lyt-map-vehicle-click',
    MAP_SIGNAL_CLICKED: 'lyt-map-signal-click',
    MAP_VEHICLE_HOVERED: 'lyt-map-vehicle-hover',
    MAP_SIGNAL_HOVERED: 'lyt-map-signal-hover',
    MAP_PRIORITY_CLICKED: 'lyt-map-priority-click',
    MAP_PRIORITY_HOVERED: 'lyt-map-priority-hover',
    MAP_RAIL_TSP_HOVERED: 'lyt-map-rail-tsp-hover',
    MAP_EVP_VEHICLE_CLICKED: 'lyt-map-evp-vehicle-click',
    MAP_EVP_INCIDENT_CLICKED: 'lyt-map-evp-incident-click',

    // Dashboard Interaction Events
    DASHBOARD_TIME_PERIOD_CHANGED: 'lyt-dashboard-time-period-change',
    DASHBOARD_DATE_RANGE_SELECTED: 'lyt-dashboard-date-range-select',
    DASHBOARD_DATA_EXPORTED: 'lyt-dashboard-data-export',
    DASHBOARD_CHART_FILTER_CHANGED: 'lyt-dashboard-chart-filter-change',
    DASHBOARD_NAVIGATION_CLICKED: 'lyt-dashboard-navigation-click',
    DASHBOARD_ALERT_CLICKED: 'lyt-dashboard-alert-click',
    DASHBOARD_ALERT_STATUS_CHANGED: 'lyt-dashboard-alert-status-change',
    DASHBOARD_CHART_LEGEND_CLICKED: 'lyt-dashboard-chart-legend-click',
    DASHBOARD_WIDGET_TOGGLED: 'lyt-dashboard-widget-toggle',
    DASHBOARD_ERROR_BOUNDARY_TRIGGERED: 'lyt-dashboard-error-boundary-trigger',
    DASHBOARD_CHART_REFRESHED: 'lyt-dashboard-chart-refresh',

    // Trip Analyzer (TTA) — names follow the Confluence spec
    // "Trip Analyzer — User Analytics". GA4/Clarity params are coarse buckets;
    // trip, route, and signal identifiers go to the ops log (SigNoz) only.

    // Tier 1 — adoption and core funnel
    TTA_PAGE_VIEWED: 'lyt-tta-page-view',
    TTA_DATE_SELECTED: 'lyt-tta-date-select',
    TTA_ROUTE_SELECTED: 'lyt-tta-route-select',
    TTA_DIRECTION_SELECTED: 'lyt-tta-direction-select',
    TTA_TRIPS_SELECTED: 'lyt-tta-trip-select',
    TTA_TIMELINE_LOADED: 'lyt-tta-timeline-load',
    TTA_SETUP_ABANDONED: 'lyt-tta-setup-abandon',

    // Tier 2 — feature engagement
    TTA_TIME_WINDOW_CHANGED: 'lyt-tta-time-window-change',
    TTA_ON_TIME_WINDOW_CHANGED: 'lyt-tta-on-time-window-change',
    TTA_CONTROLS_OPENED: 'lyt-tta-controls-open',
    TTA_CONTROLS_TOGGLED: 'lyt-tta-controls-toggle',
    TTA_LEGEND_TOGGLED: 'lyt-tta-legend-toggle',
    TTA_PARTNER_FILTER_CHANGED: 'lyt-tta-partner-filter-change',
    TTA_SIGNAL_CLICKED: 'lyt-tta-signal-click',
    TTA_TSP_PANEL_OPENED: 'lyt-tta-tsp-panel-open',
    TTA_TSP_PANEL_CLOSED: 'lyt-tta-tsp-panel-close',
    TTA_TRIPS_REORDERED: 'lyt-tta-trip-reorder',
    TTA_COMPARE_TRIP_ADDED: 'lyt-tta-compare-trip-add',
    TTA_COMPARE_TRIP_REMOVED: 'lyt-tta-compare-trip-remove',
    TTA_COMPARE_VISIBILITY_CHANGED: 'lyt-tta-compare-visibility-change',

    // Tier 3 — friction and empty states
    TTA_EMPTY_SETUP_SHOWN: 'lyt-tta-empty-setup',
    TTA_EMPTY_NO_DATA_SHOWN: 'lyt-tta-empty-no-data',
    TTA_EMPTY_RESTRICTED_SHOWN: 'lyt-tta-empty-restricted',
    TTA_ERROR_DISPLAYED: 'lyt-tta-error-display',
    TTA_ERROR_RETRIED: 'lyt-tta-error-retry',
};

export default ANALYTIC_EVENTS;